// src/pages/Dashboard/Alumnos/components/EditarResponsablesModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Row, Col, message, Spin, Tag, Card, Checkbox } from 'antd';
import { TeamOutlined, SaveOutlined } from '@ant-design/icons';
import apiClient from '../../../../api/apiClient';

const EditarResponsablesModal = ({ open, onCancel, idFamilia, onResponsablesActualizados }) => {
  const [responsables, setResponsables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Cargar responsables al abrir
  useEffect(() => {
    const cargar = async () => {
      if (!idFamilia || !open) return;
      setLoading(true);
      try {
        const res = await apiClient.get(`/responsables/familia/${idFamilia}`);
        const lista = Array.isArray(res.data) ? res.data : res.data.data || [];
        setResponsables(lista.map(r => ({ ...r })));
      } catch (error) {
        message.error('Error al cargar responsables');
        setResponsables([]);
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [idFamilia, open]);

  // Limpiar al cerrar
  useEffect(() => {
    if (!open) {
      setResponsables([]);
      setSaving(false);
    }
  }, [open]);

  // Actualizar campo de un responsable localmente
  const actualizarCampo = (index, campo, valor) => {
    setResponsables(prev => {
      const copia = [...prev];
      copia[index] = { ...copia[index], [campo]: valor };
      return copia;
    });
  };

  // Manejar cambio de EsResponsable (solo uno puede ser principal)
  const handleEsResponsableChange = (index, checked) => {
    setResponsables(prev => {
      return prev.map((r, i) => ({
        ...r,
        EsResponsable: i === index ? checked : (checked ? false : r.EsResponsable),
      }));
    });
  };

  // Guardar TODOS los responsables
  const handleGuardarTodos = async () => {
    const userFromStorage = JSON.parse(localStorage.getItem('user') || '{}');
    const IdColaborador = userFromStorage.IdUsuario;

    if (!IdColaborador) {
      message.error('No se encontró el usuario en sesión.');
      return;
    }

    setSaving(true);
    try {
      // Enviar PUT para cada responsable
      await Promise.all(
        responsables
          .filter(r => r.IdResponsable)
          .map(r =>
            apiClient.put(`/responsables/${r.IdResponsable}`, {
              NombreResponsable: r.NombreResponsable,
              DPI: r.DPI || null,
              NIT: r.NIT || null,
              EsResponsable: r.EsResponsable ? 1 : 0,
              IdColaborador,
            })
          )
      );

      message.success('Responsables actualizados correctamente');
      if (onResponsablesActualizados) onResponsablesActualizados();
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.message || 'Error al actualizar responsables';
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={
        <span>
          <TeamOutlined style={{ marginRight: 8 }} />
          Editar Responsables
        </span>
      }
      open={open}
      onCancel={onCancel}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={onCancel}>Cerrar</Button>
          {responsables.length > 0 && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={handleGuardarTodos}
            >
              Guardar Todos
            </Button>
          )}
        </div>
      }
      width={700}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : responsables.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>
          Esta familia no tiene responsables registrados
        </div>
      ) : (
        responsables.map((r, index) => (
          <Card
            key={r.IdResponsable || index}
            size="small"
            style={{
              marginBottom: 12,
              background: r.EsResponsable ? '#e6f7ff' : '#fafafa',
              border: r.EsResponsable ? '1px solid #91d5ff' : '1px solid #f0f0f0',
            }}
            title={
              <span>
                <Tag color="geekblue">{r.TipoResponsable || 'Responsable'}</Tag>
                {r.EsResponsable ? <Tag color="blue">Principal</Tag> : null}
              </span>
            }
          >
            <Row gutter={16} align="middle">
              <Col span={10}>
                <label style={{ fontWeight: 500, fontSize: 12, color: '#666' }}>Nombre Responsable</label>
                <Input
                  value={r.NombreResponsable || ''}
                  onChange={(e) => actualizarCampo(index, 'NombreResponsable', e.target.value)}
                  placeholder="Nombre completo"
                />
              </Col>
              <Col span={5}>
                <label style={{ fontWeight: 500, fontSize: 12, color: '#666' }}>DPI</label>
                <Input
                  value={r.DPI || ''}
                  onChange={(e) => actualizarCampo(index, 'DPI', e.target.value)}
                  placeholder="DPI"
                />
              </Col>
              <Col span={5}>
                <label style={{ fontWeight: 500, fontSize: 12, color: '#666' }}>NIT</label>
                <Input
                  value={r.NIT || ''}
                  onChange={(e) => actualizarCampo(index, 'NIT', e.target.value)}
                  placeholder="NIT"
                />
              </Col>
              <Col span={4} style={{ textAlign: 'center' }}>
                <label style={{ fontWeight: 500, fontSize: 12, color: '#666', display: 'block' }}>Principal</label>
                <Checkbox
                  checked={!!r.EsResponsable}
                  onChange={(e) => handleEsResponsableChange(index, e.target.checked)}
                  style={{ marginTop: 4 }}
                />
              </Col>
            </Row>
          </Card>
        ))
      )}
    </Modal>
  );
};

export default EditarResponsablesModal;
